import { TEAM_COLOR } from '@fisura/shared';
import { RosterFlag, type RosterEntry } from '@fisura/protocol';

const css = (c: [number, number, number]): string =>
  `rgb(${Math.round(c[0] * 255)},${Math.round(c[1] * 255)},${Math.round(c[2] * 255)})`;

const MONO = 'ui-monospace, "SF Mono", Menlo, monospace';
const TEAM_NAME = ['FISURA', 'POLICE'];

export interface KillSide {
  name: string;
  color: string;
}

/**
 * Team-deathmatch combat HUD — health bar, ammo/weapon, team scores, kill feed
 * and the death overlay. Builds its own DOM so it stays decoupled from the perf
 * HUD (hud.ts) and index.html.
 */
export class CombatHud {
  private readonly root: HTMLDivElement;
  private readonly hpFill: HTMLDivElement;
  private readonly hpText: HTMLSpanElement;
  private readonly ammo: HTMLDivElement;
  private readonly nades: HTMLDivElement;
  private readonly blindEl: HTMLDivElement;
  private readonly wind: HTMLDivElement;
  private windDir = 0;
  private windSpeed = 0;
  private readonly scores: HTMLDivElement;
  private readonly timer: HTMLDivElement;
  private readonly banner: HTMLDivElement;
  private readonly feed: HTMLDivElement;
  private readonly death: HTMLDivElement;
  private readonly respawn: HTMLDivElement;
  private readonly flash: HTMLDivElement;
  private readonly vignette: HTMLDivElement;
  private readonly teamBadge: HTMLDivElement;
  private readonly teamIntro: HTMLDivElement;
  private readonly countdown: HTMLDivElement;
  private introTimer: ReturnType<typeof setTimeout> | null = null;
  private suppress!: HTMLDivElement;
  private readonly indicatorLayer: HTMLDivElement;
  private scoreboard!: HTMLDivElement;
  private readonly feedLines: string[] = [];
  private flashUntil = 0;
  private readonly hits: { dirYaw: number; until: number; el: HTMLDivElement }[] = [];

  constructor() {
    this.root = el('div', { position: 'fixed', inset: '0', zIndex: '12', pointerEvents: 'none' });

    // --- Health bar (bottom-left) ---
    const hpWrap = el('div', {
      position: 'fixed', left: '20px', bottom: '20px', fontFamily: MONO, color: '#fff',
    });
    // Which side am I on? — always visible, right above the health bar.
    this.teamBadge = el('div', {
      fontSize: '13px', fontWeight: '700', letterSpacing: '2px', marginBottom: '6px',
      textShadow: '0 1px 3px rgba(0,0,0,0.9)',
    });
    hpWrap.appendChild(this.teamBadge);
    const hpLabel = el('div', { fontSize: '11px', letterSpacing: '3px', color: '#b9b9c0', marginBottom: '4px' });
    hpLabel.textContent = 'HEALTH';
    hpWrap.appendChild(hpLabel);
    const bar = el('div', {
      width: '230px', height: '16px', background: 'rgba(0,0,0,0.45)',
      border: '1px solid #2a2a32', borderRadius: '3px', overflow: 'hidden',
    });
    this.hpFill = el('div', { height: '100%', width: '100%', background: '#5ad15a', transition: 'width 90ms linear' });
    bar.appendChild(this.hpFill);
    hpWrap.appendChild(bar);
    this.hpText = document.createElement('span');
    Object.assign(this.hpText.style, { fontSize: '22px', fontWeight: '700', color: '#fff' });
    const hpNum = el('div', { marginTop: '2px' });
    hpNum.appendChild(this.hpText);
    hpWrap.appendChild(hpNum);
    this.root.appendChild(hpWrap);

    // --- Ammo / weapon (bottom-right) ---
    this.ammo = el('div', {
      position: 'fixed', right: '22px', bottom: '20px', fontFamily: MONO,
      color: '#fff', textAlign: 'right', textShadow: '0 1px 3px rgba(0,0,0,0.9)',
    });
    this.root.appendChild(this.ammo);

    // --- Grenade loadout (above the ammo, bottom-right) ---
    this.nades = el('div', {
      position: 'fixed', right: '22px', bottom: '74px', fontFamily: MONO,
      fontSize: '12px', textAlign: 'right', letterSpacing: '1px',
      textShadow: '0 1px 3px rgba(0,0,0,0.9)',
    });
    this.root.appendChild(this.nades);

    // --- Wind gauge (top-left): arrow is view-relative so it matches the drift
    //     you actually see, plus an intensity word + speed. ---
    this.wind = el('div', {
      position: 'fixed', left: '20px', top: '18px', fontFamily: MONO,
      fontSize: '12px', letterSpacing: '1px', color: '#cdd0d8',
      textShadow: '0 1px 3px rgba(0,0,0,0.9)',
    });
    this.root.appendChild(this.wind);

    // --- Team scores (top-center) + match timer ---
    this.scores = el('div', {
      position: 'fixed', top: '12px', left: '50%', transform: 'translateX(-50%)',
      fontFamily: MONO, fontSize: '22px', fontWeight: '700', textShadow: '0 1px 4px rgba(0,0,0,0.9)',
    });
    this.root.appendChild(this.scores);
    this.timer = el('div', {
      position: 'fixed', top: '42px', left: '50%', transform: 'translateX(-50%)',
      fontFamily: MONO, fontSize: '14px', color: '#cfcfd6', textShadow: '0 1px 3px rgba(0,0,0,0.9)',
    });
    this.root.appendChild(this.timer);

    // --- Match banner (win / intermission) ---
    this.banner = el('div', {
      position: 'fixed', top: '30%', left: '50%', transform: 'translate(-50%,-50%)',
      fontFamily: MONO, fontSize: '42px', fontWeight: '700', letterSpacing: '3px',
      textShadow: '0 2px 8px rgba(0,0,0,0.95)', display: 'none', textAlign: 'center',
    });
    this.root.appendChild(this.banner);

    // --- Team intro (shown on spawn-in so you instantly know your side) ---
    this.teamIntro = el('div', {
      position: 'fixed', top: '24%', left: '50%', transform: 'translate(-50%,-50%)',
      fontFamily: MONO, textAlign: 'center', opacity: '0',
      transition: 'opacity 400ms ease', pointerEvents: 'none',
      textShadow: '0 2px 8px rgba(0,0,0,0.95)',
    });
    this.root.appendChild(this.teamIntro);

    // --- Pre-match 3-2-1 countdown (big centred number) ---
    this.countdown = el('div', {
      position: 'fixed', top: '42%', left: '50%', transform: 'translate(-50%,-50%)',
      fontFamily: MONO, textAlign: 'center', display: 'none', pointerEvents: 'none',
      textShadow: '0 2px 16px rgba(0,0,0,0.95)',
    });
    this.root.appendChild(this.countdown);

    // --- Damage feedback: red flash + low-HP vignette + directional hits ---
    this.flash = el('div', {
      position: 'fixed', inset: '0', background: 'rgba(255,30,20,0.28)',
      opacity: '0', transition: 'opacity 90ms linear',
    });
    this.root.appendChild(this.flash);
    this.vignette = el('div', {
      position: 'fixed', inset: '0', opacity: '0',
      background: 'radial-gradient(ellipse at center, rgba(0,0,0,0) 55%, rgba(180,10,10,0.55) 100%)',
    });
    this.root.appendChild(this.vignette);
    // Suppression / focus-loss: dark tunnel-vision creeping in from the edges.
    this.suppress = el('div', {
      position: 'fixed', inset: '0', opacity: '0',
      background: 'radial-gradient(ellipse at center, rgba(0,0,0,0) 40%, rgba(10,10,18,0.85) 100%)',
    });
    this.root.appendChild(this.suppress);
    // Flashbang white-out: full-screen white that fades as the blind decays.
    this.blindEl = el('div', {
      position: 'fixed', inset: '0', opacity: '0', background: '#fff',
      transition: 'opacity 60ms linear',
    });
    this.root.appendChild(this.blindEl);
    this.indicatorLayer = el('div', { position: 'fixed', inset: '0' });
    this.root.appendChild(this.indicatorLayer);

    // --- Tab scoreboard ---
    this.scoreboard = el('div', {
      position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
      background: 'rgba(10,10,14,0.88)', border: '1px solid #2a2a32', borderRadius: '8px',
      padding: '14px 22px', fontFamily: MONO, fontSize: '13px', color: '#e8e8ec',
      display: 'none', minWidth: '420px',
    });
    this.root.appendChild(this.scoreboard);

    // --- Kill feed (top-right) ---
    this.feed = el('div', {
      position: 'fixed', right: '14px', top: '52px', fontFamily: MONO, fontSize: '13px',
      textAlign: 'right', lineHeight: '1.7', textShadow: '0 1px 3px rgba(0,0,0,0.9)',
    });
    this.root.appendChild(this.feed);

    // --- Death overlay ---
    this.death = el('div', {
      position: 'fixed', inset: '0', display: 'none', alignItems: 'center',
      justifyContent: 'center', flexDirection: 'column', background: 'rgba(40,6,6,0.45)',
      color: '#fff', fontFamily: MONO,
    });
    const h = el('div', { fontSize: '40px', letterSpacing: '3px', color: '#ff5252', fontWeight: '700' });
    h.textContent = 'YOU DIED';
    this.respawn = el('div', { marginTop: '10px', color: '#d8d8dc', fontSize: '15px' });
    this.death.appendChild(h);
    this.death.appendChild(this.respawn);
    this.root.appendChild(this.death);

    document.body.appendChild(this.root);
  }

  setVisible(on: boolean): void {
    this.root.style.display = on ? 'block' : 'none';
  }

  /** Persistent "YOU ARE <TEAM>" chip above the health bar. */
  setTeam(teamId: number): void {
    const c = css(TEAM_COLOR[teamId] ?? [0.8, 0.8, 0.8]);
    this.teamBadge.innerHTML =
      `<span style="color:#b9b9c0;font-weight:400">YOU ARE</span> <span style="color:${c}">◤ ${TEAM_NAME[teamId] ?? '?'} ◢</span>`;
  }

  /** Big center splash on (re)spawn-in: your team, its color, and the target. */
  showTeamIntro(teamId: number): void {
    const enemy = teamId === 0 ? 1 : 0;
    const c = css(TEAM_COLOR[teamId] ?? [0.8, 0.8, 0.8]);
    const ce = css(TEAM_COLOR[enemy] ?? [0.8, 0.8, 0.8]);
    this.teamIntro.innerHTML =
      `<div style="font-size:44px;font-weight:700;letter-spacing:4px;color:${c}">YOU ARE ${TEAM_NAME[teamId] ?? '?'}</div>` +
      `<div style="font-size:16px;color:#cfcfd6;margin-top:6px">Hunt down the <span style="color:${ce};font-weight:700">${TEAM_NAME[enemy]}</span> — your teammates are <span style="color:${c}">${TEAM_NAME[teamId]}</span>-colored with names overhead</div>`;
    this.teamIntro.style.opacity = '1';
    if (this.introTimer) clearTimeout(this.introTimer);
    this.introTimer = setTimeout(() => {
      this.teamIntro.style.opacity = '0';
    }, 4000);
  }

  /** Big centred "3 / 2 / 1" during the pre-match freeze; pass null to hide. */
  setCountdown(n: number | null): void {
    if (n === null || n <= 0) {
      this.countdown.style.display = 'none';
      return;
    }
    this.countdown.style.display = 'block';
    this.countdown.innerHTML =
      `<div style="font-size:140px;font-weight:700;line-height:1;color:#fff">${n}</div>` +
      `<div style="font-size:18px;letter-spacing:6px;color:#ff7a1a;margin-top:8px">GET READY</div>`;
  }

  setHealth(hp: number): void {
    const pct = Math.max(0, Math.min(100, hp));
    this.hpFill.style.width = `${pct}%`;
    this.hpFill.style.background = pct > 60 ? '#5ad15a' : pct > 30 ? '#e0b020' : '#e0463c';
    this.hpText.textContent = String(Math.round(pct));
  }

  setAmmo(cur: number, mag: number, weaponName: string, reloading: boolean): void {
    this.ammo.innerHTML = reloading
      ? `<span style="color:#e0b020">RELOADING…</span><br><span style="color:#9a9aa2;font-size:12px">${weaponName}</span>`
      : `<span style="font-size:26px;font-weight:700">${cur}</span> <span style="color:#9a9aa2">/ ${mag}</span><br><span style="color:#cfcfd6;font-size:12px;letter-spacing:1px">${weaponName}</span>`;
  }

  /**
   * Grenade loadout strip: the selected type is boxed/bright, available ones are
   * green, and spent ones are struck through and greyed. `stock` is indexed by
   * GrenadeType (0=Frag 1=Smoke 2=Flashbang).
   */
  setGrenades(selected: number, stock: number[]): void {
    const names = ['FRAG', 'SMOKE', 'FLASH'];
    const chips = names.map((n, i) => {
      const have = (stock[i] ?? 0) > 0;
      const color = !have ? '#555' : i === selected ? '#ffd24a' : '#7ad17a';
      const deco = have ? 'none' : 'line-through';
      const box = i === selected ? 'border:1px solid #ffd24a;border-radius:3px;padding:0 4px' : 'padding:0 4px';
      return `<span style="color:${color};text-decoration:${deco};${box}">${n}</span>`;
    });
    this.nades.innerHTML =
      `<span style="color:#8a8a92">GRENADE</span> ${chips.join(' ')}` +
      `<br><span style="color:#6a6a72;font-size:10px">[G] throw · [B] cycle</span>`;
  }

  /** Flashbang white-out (0..1). Slight gamma so it whites out hard up close. */
  setBlind(intensity: number): void {
    this.blindEl.style.opacity = String(Math.min(1, Math.max(0, intensity) * 1.35));
  }

  /** Current wind (world yaw it blows toward + speed u/s); rendered in update(). */
  setWind(dir: number, speed: number): void {
    this.windDir = dir;
    this.windSpeed = speed;
  }

  private renderWind(viewYaw: number): void {
    const s = this.windSpeed;
    const word = s < 2 ? 'Calm' : s < 3.5 ? 'Light' : s < 5 ? 'Moderate' : s < 6.5 ? 'Strong' : 'Gale';
    const color = s < 2 ? '#8a8a92' : s < 5 ? '#7ab0d1' : '#e0b020';
    // View-relative bearing: screen-up = where you're looking, so the arrow
    // points the way the wind is actually pushing things on your screen.
    const bearing = this.windDir - viewYaw;
    this.wind.innerHTML =
      `<span style="color:#8a8a92">WIND</span> ` +
      `<span style="display:inline-block;transform:rotate(${bearing}rad);color:${color};font-size:15px">↑</span> ` +
      `<span style="color:${color}">${word}</span> ` +
      `<span style="color:#9a9aa2">${s.toFixed(1)} u/s</span>`;
  }

  setScores(team0: number, team1: number, myTeam: number): void {
    const c0 = css(TEAM_COLOR[0]!);
    const c1 = css(TEAM_COLOR[1]!);
    const mark = (t: number): string => (t === myTeam ? '▸' : '');
    this.scores.innerHTML =
      `<span style="color:${c0}">${mark(0)}FISURA ${team0}</span>` +
      `<span style="color:#888;margin:0 12px">:</span>` +
      `<span style="color:${c1}">${team1} POLICE${mark(1)}</span>`;
  }

  pushKill(killer: KillSide, victim: KillSide, weaponName: string, headshot: boolean, ff: boolean): void {
    const tags = `${headshot ? ' <span style="color:#fff">☠</span>' : ''}${ff ? ' <span style="color:#e0b020">⚠FF</span>' : ''}`;
    this.feedLines.push(
      `<span style="color:${killer.color}">${killer.name}</span> <span style="color:#bbb">[${weaponName}]</span> ✕ <span style="color:${victim.color}">${victim.name}</span>${tags}`,
    );
    while (this.feedLines.length > 5) this.feedLines.shift();
    this.feed.innerHTML = this.feedLines.join('<br>');
  }

  setDead(dead: boolean, secondsLeft: number): void {
    this.death.style.display = dead ? 'flex' : 'none';
    if (dead) this.respawn.textContent = `Respawning in ${secondsLeft.toFixed(1)}s`;
  }

  /** Show/refresh the Tab scoreboard from the roster (sorted by kills). */
  setScoreboard(visible: boolean, roster: RosterEntry[], myId: number | null): void {
    this.scoreboard.style.display = visible ? 'block' : 'none';
    if (!visible) return;
    const rows = [...roster]
      .sort((a, b) => b.kills - a.kills || a.deaths - b.deaths)
      .map((p) => {
        const c = css(TEAM_COLOR[p.teamId] ?? [0.8, 0.8, 0.8]);
        const me = p.id === myId;
        const bot = (p.flags & RosterFlag.Bot) !== 0 ? ' <span style="color:#777">BOT</span>' : '';
        return `<tr style="${me ? 'background:rgba(255,255,255,0.08)' : ''}">
          <td style="color:${c};padding:2px 14px 2px 4px;text-align:left">${escapeHtml(p.name)}${bot}</td>
          <td style="padding:2px 12px;text-align:right">${p.kills}</td>
          <td style="padding:2px 4px;text-align:right;color:#9a9aa2">${p.deaths}</td>
        </tr>`;
      })
      .join('');
    this.scoreboard.innerHTML =
      `<div style="text-align:center;letter-spacing:3px;color:#b9b9c0;margin-bottom:8px">SCOREBOARD</div>` +
      `<table style="width:100%;border-collapse:collapse">
        <tr style="color:#9a9aa2"><th style="text-align:left;padding:0 14px 6px 4px">PLAYER</th>
        <th style="text-align:right;padding:0 12px 6px">K</th><th style="text-align:right;padding:0 4px 6px">D</th></tr>
        ${rows}
      </table>`;
  }

  /** Red screen flash + a directional indicator toward the attacker. */
  showDamage(dirYaw: number): void {
    this.flashUntil = performance.now() + 140;
    this.flash.style.opacity = '1';
    const arrow = el('div', {
      position: 'fixed', left: '50%', top: '50%', width: '0', height: '0',
      borderLeft: '14px solid transparent', borderRight: '14px solid transparent',
      borderBottom: '22px solid rgba(255,60,40,0.9)',
    });
    this.indicatorLayer.appendChild(arrow);
    this.hits.push({ dirYaw, until: performance.now() + 1100, el: arrow });
  }

  setTimer(msLeft: number, killTarget: number): void {
    const s = Math.max(0, Math.ceil(msLeft / 1000));
    const mm = Math.floor(s / 60);
    const ss = String(s % 60).padStart(2, '0');
    this.timer.textContent = `${mm}:${ss} · first to ${killTarget}`;
  }

  setBanner(text: string, color: string): void {
    this.banner.style.display = text ? 'block' : 'none';
    this.banner.style.color = color;
    this.banner.textContent = text;
  }

  /** Per-frame: fade the flash, rotate indicators view-relative, drive vignettes. */
  update(viewYaw: number, hp: number, suppression = 0, focusHeat = 0): void {
    const now = performance.now();
    if (this.flashUntil && now >= this.flashUntil) {
      this.flash.style.opacity = '0';
      this.flashUntil = 0;
    }
    // Low-HP vignette ramps below 40 HP.
    this.vignette.style.opacity = hp < 40 ? String(Math.min(1, (40 - hp) / 40 + 0.2)) : '0';
    // Suppression + sustained-fire focus loss: tunnel vision from the edges.
    this.suppress.style.opacity = String(Math.min(0.95, suppression * 0.9 + focusHeat * 0.3));
    this.renderWind(viewYaw);

    for (let i = this.hits.length - 1; i >= 0; i--) {
      const h = this.hits[i]!;
      if (now >= h.until) {
        h.el.remove();
        this.hits.splice(i, 1);
        continue;
      }
      // Screen angle of the attacker relative to where we look (0 = ahead).
      const rel = h.dirYaw - viewYaw;
      const dist = 120;
      const x = Math.sin(rel) * dist;
      const y = -Math.cos(rel) * dist;
      h.el.style.transform = `translate(${x - 14}px, ${y - 11}px) rotate(${rel}rad)`;
      h.el.style.opacity = String(Math.max(0.15, (h.until - now) / 1100));
    }
  }
}

function el(tag: string, style: Partial<CSSStyleDeclaration>): HTMLDivElement {
  const d = document.createElement(tag) as HTMLDivElement;
  Object.assign(d.style, style);
  return d;
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
